import type { RenderFrame } from '../render/frame';
import type { SimEvents } from '../sim/events';
import { RoundPhase } from '../sim/traits';

type Sample = { at: number; frame: RenderFrame };
type FighterHighlights = { kos: number; biggestHit: number; perfectBlocks: number };

const SAMPLE_MS = 1000 / 60;
const SAMPLE_TOLERANCE_MS = 2;
const HISTORY_MS = 2000;
const MAX_SAMPLES = 121;

/** A bounded visual replay: gameplay never rewinds and online input sequencing stays untouched. */
export function createFinale() {
  let samples: Sample[] = [];
  let playback: Sample[] = [];
  let nextSampleAt = -1;
  let startedAt = -1;
  let finished = false;
  let level = '';
  const stats = new Map<number, FighterHighlights>();
  const fighter = (slot: number) => {
    if (!stats.has(slot)) stats.set(slot, { kos: 0, biggestHit: 0, perfectBlocks: 0 });
    return stats.get(slot)!;
  };
  const copyFrame = (frame: RenderFrame): RenderFrame => ({
    groups: frame.groups.map((g) => ({ ...g, primitives: g.primitives.map((p) => ({ ...p })) })),
    camera: { ...frame.camera, shakeX: 0, shakeY: 0 },
    theme: { ...frame.theme },
    lights: frame.lights?.map((light) => ({ ...light })),
    hud: { slowmo: false, countdown: 0 },
  });
  return {
    reset() { samples = []; playback = []; stats.clear(); nextSampleAt = -1; startedAt = -1; finished = false; level = ''; },
    observe(events: SimEvents, slotOf: (entity: number) => number | undefined) {
      for (const event of events) {
        if (event.type === 'kill') {
          const slot = slotOf(event.source), target = slotOf(event.target);
          if (slot !== undefined && target !== undefined && slot !== target) fighter(slot).kos++;
        } else if (event.type === 'hit') {
          const slot = slotOf(event.source), target = slotOf(event.target);
          if (slot !== undefined && target !== undefined && slot !== target) fighter(slot).biggestHit = Math.max(fighter(slot).biggestHit, event.damage);
        } else if (event.type === 'block' && event.reflected) {
          const slot = slotOf(event.player);
          if (slot !== undefined) fighter(slot).perfectBlocks++;
        }
      }
    },
    capture(frame: RenderFrame, now: number, enabled: boolean) {
      if (!enabled) { samples = []; playback = []; nextSampleAt = -1; startedAt = -1; return; }
      if (frame.hud.levelName !== level) { samples = []; playback = []; nextSampleAt = -1; startedAt = -1; level = frame.hud.levelName ?? ''; }
      const phase = frame.hud.phase;
      if (phase === RoundPhase.Loading || phase === RoundPhase.Countdown) { samples = []; nextSampleAt = -1; return; }
      if (finished || (phase !== RoundPhase.Fighting && phase !== RoundPhase.LastKill && phase !== RoundPhase.MatchOver)) return;
      if (frame.groups.length > 2000) return;
      // Keep a 60 Hz schedule, allowing minor rAF jitter instead of accidentally sampling every
      // other display frame. Advance past missed slots after a hitch; never queue catch-up copies.
      if (nextSampleAt >= 0 && now < nextSampleAt - SAMPLE_TOLERANCE_MS && phase !== RoundPhase.MatchOver) return;
      nextSampleAt = nextSampleAt < 0 ? now + SAMPLE_MS
        : nextSampleAt + Math.max(1, Math.floor((now - nextSampleAt) / SAMPLE_MS) + 1) * SAMPLE_MS;
      samples.push({ at: now, frame: copyFrame(frame) });
      // Two seconds at 60 fps; old arenas and live particle/decal pools are never retained.
      while (samples.length > MAX_SAMPLES || (samples[0] && now - samples[0].at > HISTORY_MS)) samples.shift();
    },
    start(now: number, enabled: boolean) {
      if (finished) return;
      finished = true;
      if (!enabled || samples.length < 3) { samples = []; return; }
      playback = samples; samples = []; startedAt = now;
    },
    frame(now: number): RenderFrame | null {
      if (startedAt < 0 || !playback.length) return null;
      const first = playback[0]!.at, duration = playback[playback.length - 1]!.at - first;
      // Play at normal speed: slowing sparse snapshots made the finish feel like dropped frames.
      const elapsed = now - startedAt;
      if (elapsed > duration + 250) { playback = []; startedAt = -1; return null; }
      let index = playback.findIndex((sample) => sample.at - first > elapsed);
      if (index === -1) index = playback.length;
      const before = playback[Math.max(0, index - 1)]!, after = playback[index];
      // Nearest snapshot absorbs timestamp jitter at playback's display cadence. Geometry is
      // never interpolated between unrelated groups (fighters and projectiles can appear/disappear).
      return after && after.at - first - elapsed < elapsed - (before.at - first)
        ? after.frame : before.frame;
    },
    skip() { playback = []; startedAt = -1; },
    get active() { return startedAt >= 0; },
    get bufferedFrames() { return samples.length + playback.length; },
    highlights(name: (slot: number) => string): string[] {
      const lines: string[] = [];
      for (const [key, title] of [['kos', 'Most knockouts'], ['biggestHit', 'Biggest hit'], ['perfectBlocks', 'Perfect blocks']] as const) {
        const max = Math.max(0, ...[...stats.values()].map((s) => s[key]));
        if (max <= 0) continue;
        const leaders = [...stats.entries()].filter(([, s]) => s[key] === max).map(([slot]) => name(slot));
        lines.push(`${title} · ${leaders.join(' + ')} · ${Math.round(max)}`);
      }
      return lines;
    },
  };
}
